// Driver training hours — the read side of #1580.
//
// A member logs supervised right-seat hours against ONE rig at a time
// (`driverLogs/{id}.apparatus`), counted per rig: 20 hours on Engine 1 says nothing about
// Engine 2. The hours FLOOR is one figure for the whole fleet plus a per-type checkbox saying
// where it applies — departments run one standard, not nine — with an optional per-rig override
// for the one awkward truck.
//
// Reaching that floor does NOT clear anyone. Clearance is an officer's explicit per-rig
// decision (driverClearances/{uid}); the hours are the evidence they weigh. See
// driverClearance() below for why it works that way.
//
// Deliberately dependency-free, for the same reason ./driverOperator.js is: the
// department app, the hub matrix and the Cloud Run api all need this arithmetic, and
// keeping it in @embertracking/ui rather than @embertracking/common means shipping it
// needs no npm publish (see the publish table in CLAUDE.md).
//
// embertracking/src/data/driverHours.js re-exports this as the app's import path.

// Statuses a log moves through. A member's own entry lands PENDING and contributes
// nothing until an officer approves it; an officer's entry lands APPROVED immediately.
export const LOG_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected'
};

// The 2020 `driverLogs` experiment (PR #87, stripped in #251) left real documents behind
// on both production tenants, and they carry no `status` at all — there was no approval
// workflow to have a status for. An absent status therefore reads as APPROVED: those
// hours were entered by an officer on the only surface that existed. This is what lets
// the feature reuse the collection with no migration script.
export const logStatus = log => log?.status || LOG_STATUS.APPROVED;

export const isApproved = log => logStatus(log) === LOG_STATUS.APPROVED;
export const isPending = log => logStatus(log) === LOG_STATUS.PENDING;

const num = value => {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

// A positive target, or null. Zero and negative are normalised to null — "0 hours
// required" and "not tracked" are the same statement, and collapsing them here means
// every caller can test `target === null` rather than `!target`, which would also swallow
// a legitimately unset value differently depending on whether it was 0 or undefined.
const positiveTarget = value => {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Apparatus types a member can be cleared to DRIVE, in the order Settings lists them.
//
// A TRAILER is towed rather than driven, and a BOAT isn't a road apparatus — neither belongs
// in a driver-training programme, so neither is offered in Settings and neither can ever
// resolve a target, whatever a stored `types` map or a stray per-rig override says. Gating
// here rather than only in the UI means bad data can't put a trailer column on the grid.
export const DRIVER_TRAINABLE_TYPES = ['ENGINE', 'PUMPER', 'TENDER', 'BUSH', 'LADDER', 'RESCUE', 'UTILITY', 'CAR'];

export const isDriverTrainableType = type => DRIVER_TRAINABLE_TYPES.includes(type);

// How many supervised hours this specific rig needs before a member is cleared on it.
//
// One hours figure covers the whole fleet and a per-type checkbox decides which types it
// applies to — departments run one standard, not nine. Exceptions are per RIG rather than
// per type (`apparatus.driverHoursTarget`), because in practice it's one awkward truck that
// differs, not a whole class of them.
//
// The per-rig override wins outright, including when it's explicitly 0 — a department that
// sets Utility 1 to 0 is saying "this one doesn't need hours" and must not fall through to
// the department figure. Hence the `!= null` test rather than `??` on the normalised value:
// normalising first would turn an intentional 0 into null and then inherit, which is the
// opposite of what was asked for. It also wins over an unchecked type, so a single rig can be
// tracked without switching its whole class on.
export const effectiveTarget = (apparatus, settings) => {
    if (!apparatus || !isDriverTrainableType(apparatus.type)) return null;

    const override = apparatus.driverHoursTarget;
    if (override != null && override !== '') return positiveTarget(override);

    const config = settings?.driverTrainingHours || {};
    if (config.types?.[apparatus.type] !== true) return null;

    return positiveTarget(config.hours);
};

export const isTrackingEnabled = settings => settings?.driverTrainingHours?.enabled === true;

// The rigs driving hours are tracked against: everything with a target, minus the ones
// nobody can be assigned to drive any more. `excludeStatuses` is passed in rather than
// imported so this module stays free of @embertracking/common (callers hand it
// `DecommissionedStatuses`).
export const trackedApparatus = (apparatusList, settings, {excludeStatuses = []} = {}) => {
    if (!isTrackingEnabled(settings)) return [];

    return (apparatusList || [])
        .filter(a => a && !a.archived)
        .filter(a => !excludeStatuses.includes(a.status))
        .filter(a => effectiveTarget(a, settings) !== null)
        .sort((a, b) => String(a.tag || '').localeCompare(String(b.tag || ''), undefined, {numeric: true}));
};

// How far a department has got with setting this up. Three states, because there are two
// distinct ways to have nothing to show and they need different copy:
//
//   off        — the toggle isn't ticked; the department hasn't opted in
//   no-targets — opted in, but no apparatus resolves a target, so there is nothing to
//                track against. Either no type has a number yet, or the only rigs that
//                carry one are archived/decommissioned.
//   ready      — at least one rig is tracked
//
// Exists so the Requirements list can offer a door instead of hiding the feature. Both
// non-ready states used to render as "no row at all", which meant an admin who ticked the
// setting and stopped could not find out why nothing happened — the same dead end #1579
// was filed about. Pure and dependency-free like the rest of this module so it's testable
// without React; Requirements.js is a hooked page with no unit coverage of its own.
export const driverHoursSetupState = ({settings, apparatusList, excludeStatuses = []} = {}) => {
    if (!isTrackingEnabled(settings)) return 'off';
    return trackedApparatus(apparatusList, settings, {excludeStatuses}).length ? 'ready' : 'no-targets';
};

// Sum approved hours out of a member's raw logs, both per rig and per type.
//
// `byType` is a convenience for reporting ("how much tender time has this member got at
// all?") and is NOT what clearance reads — clearance is per rig, by design. The type
// bucket comes from the log's own denormalised `apparatusType`, not from a lookup against
// the apparatus doc, so re-typing or decommissioning a rig can't retroactively move
// history into a different bucket.
export const approvedHours = logs => {
    const byApparatus = {};
    const byType = {};

    for (const log of logs || []) {
        if (!isApproved(log)) continue;

        const hours = num(log.hours);
        if (hours <= 0) continue;

        // `apparatus` may arrive as an id or as a populated doc, depending on whether the
        // caller ran it through populateKeyFromCollection.
        const apparatusId = typeof log.apparatus === 'string' ? log.apparatus : (log.apparatus?.uid || log.apparatus?.id);
        if (apparatusId) byApparatus[apparatusId] = num(byApparatus[apparatusId]) + hours;

        const type = log.apparatusType || (typeof log.apparatus === 'object' ? log.apparatus?.type : null);
        if (type) byType[type] = num(byType[type]) + hours;
    }

    return {byApparatus, byType};
};

// Progress on one rig. `pct` is capped at 100 so a member who logged 40 hours against a
// 20-hour target doesn't render a progress bar past its track.
export const rigProgress = (hours, apparatus, settings) => {
    const target = effectiveTarget(apparatus, settings);
    const logged = num(hours?.byApparatus?.[apparatus?.uid || apparatus?.id]);

    return {
        apparatus,
        logged,
        target,
        pct: target ? Math.min(100, Math.round((logged / target) * 100)) : 0,
        met: target !== null && logged >= target
    };
};

// Is this member cleared to drive each tracked rig?
//
// Clearance is an EXPLICIT DECISION, never derived. `granted` is driverClearances/{uid}
// .byApparatus — an officer recording, per rig, that this member is cleared to drive it. Nothing
// else clears anyone: not hours, not the check sheet.
//
// That's deliberate and it's the second design this went through. Deriving clearance from
// "hours met AND check sheet signed" was wrong in both directions. A member can log fifty hours
// against a twenty-hour minimum and still not be someone the chief will put behind that
// particular truck — the minimum is a floor, not a qualification. And the veteran who was
// driving for twenty years before anyone tracked hours needs clearing with no hours at all.
// Both cases are one human judgement, so the judgement is the record.
//
// Hours and the check sheet remain as EVIDENCE for whoever makes that call:
//   * `met`        — the hours floor is reached, so this member is ready to be considered
//   * `checkSheet`  — whether the OFC Driver/Operator requirement is complete
// Neither gates `cleared`. The officer reads them and decides; the UI shows them side by side.
export const driverClearance = ({hours, apparatusList, settings, checkSheetSignedOff = false, granted = {}, excludeStatuses = []}) => {
    const rigs = trackedApparatus(apparatusList, settings, {excludeStatuses});

    const byApparatus = {};
    for (const rig of rigs) {
        const rigId = rig.uid || rig.id;
        const progress = rigProgress(hours, rig, settings);
        const grant = granted?.[rigId] || null;

        byApparatus[rigId] = {
            ...progress,
            // Context for the decision, not part of it.
            checkSheet: checkSheetSignedOff,
            grant,
            cleared: Boolean(grant)
        };
    }

    const values = Object.values(byApparatus);
    return {
        byApparatus,
        rigs,
        checkSheet: checkSheetSignedOff,
        clearedCount: values.filter(v => v.cleared).length,
        // Members whose hours are in but who nobody has cleared yet — the queue an officer
        // works through, and the reason "met" is worth surfacing separately from "cleared".
        awaitingDecisionCount: values.filter(v => v.met && !v.cleared).length,
        trackedCount: values.length,
        anyCleared: values.some(v => v.cleared),
        allCleared: values.length > 0 && values.every(v => v.cleared)
    };
};

// The four buckets behind the driving matrix's filter, mirroring the skill-drill section's
// roster views. Pure and here rather than inline in the page because the definitions are the
// whole point of the control and they are easy to get subtly wrong:
//
//   outstanding — has at least one UNTOUCHED rig: not cleared, no hours against it. Per
//                 apparatus, not per member, so a member cleared on three rigs still shows up
//                 for the fourth nobody has started. This is work not begun.
//   inprogress  — not cleared on everything, but something is moving (hours logged somewhere).
//                 A member cleared on E1 and part-way through E2 belongs here.
//   complete    — cleared on every tracked rig.
//
// Outstanding and inprogress deliberately OVERLAP (a member can have one rig under way and
// another untouched) — they're lenses on the working set, not a partition. Only `all` is the
// union, and `complete` is the only one that's exclusive of the other two.
//
// `clearanceOf` maps a member to their driverClearance() result, so callers keep whatever
// keying they already have rather than this module assuming uid/id shapes.
export const driverMatrixBuckets = (members, clearanceOf) => {
    const list = members || [];
    const entries = member => Object.values(clearanceOf(member)?.byApparatus || {});

    return {
        all: list,
        outstanding: list.filter(m => entries(m).some(e => !e.cleared && !e.logged)),
        inprogress: list.filter(m => !clearanceOf(m)?.allCleared && entries(m).some(e => e.logged > 0)),
        complete: list.filter(m => clearanceOf(m)?.allCleared)
    };
};

// Member tally for the Requirements list row, mirroring sectionRosterTally in
// embertracking/src/data/skillDrills.js so the two rows read the same way:
//   completed  — cleared on at least one tracked apparatus (an officer's decision)
//   inProgress — logging hours, but nobody has cleared them on anything yet
//
// `completed` counts "cleared on ANYTHING", not "cleared on everything", and that choice is
// load-bearing. A department tracking four rigs will essentially never have a member cleared
// on all four — people drive the one or two they're assigned to — so an every-rig definition
// pins the column at 0% forever and tells an officer nothing. The question this row answers
// beside "16/18 hold Driver/Operator" is "how many of my members can drive?", and that's the
// at-least-one number. `allCleared` stays on driverClearance() for the per-member views, which
// legitimately want the strict measure.
//
// `hoursByUid` is the driverHours/{uid} projection keyed by uid; `signedOffUids` is the
// set of members whose Driver/Operator cell is signed off.
export const driverHoursMemberTally = ({members, apparatusList, settings, hoursByUid = {}, grantedByUid = {}, signedOffUids = new Set(), excludeStatuses = []}) => {
    const rigs = trackedApparatus(apparatusList, settings, {excludeStatuses});
    if (!rigs.length) return null;

    let cleared = 0;
    let inProgress = 0;

    for (const member of members || []) {
        const uid = member.uid || member.id;
        const clearance = driverClearance({
            hours: hoursByUid[uid],
            granted: grantedByUid[uid],
            apparatusList: rigs,
            settings,
            checkSheetSignedOff: signedOffUids.has(uid),
            excludeStatuses
        });

        if (clearance.anyCleared) cleared++;
        else if (Object.values(clearance.byApparatus).some(v => v.logged > 0)) inProgress++;
    }

    return {assigned: (members || []).length, completed: cleared, inProgress};
};
